// ============================================================
// UI COMPONENT LIBRARY
// File: src/components/UI.js
// Reusable components using the design system from ThemeContext
// ============================================================

import React, { useState } from 'react'
import { useTheme } from '../ThemeContext'
import { useLayout } from '../LayoutContext'

// ============================================================
// BUTTON COMPONENT
// ============================================================
export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  style = {},
  ...props
}) => {
  const { brand, radius, spacing, typography, shadows, colors } = useTheme()
  const [isHovered, setIsHovered] = useState(false)

  // Variant styles
  const variants = {
    primary: {
      backgroundColor: disabled ? '#9ca3af' : (isHovered ? brand.primaryHover : brand.primary),
      color: '#ffffff',
      border: 'none'
    },
    secondary: {
      backgroundColor: disabled ? colors.bg.tertiary : (isHovered ? colors.bg.hover : colors.bg.tertiary),
      color: colors.text.primary,
      border: `1px solid ${colors.border.primary}`
    },
    success: {
      backgroundColor: disabled ? '#9ca3af' : (isHovered ? brand.successHover : brand.success),
      color: '#ffffff',
      border: 'none'
    },
    danger: {
      backgroundColor: disabled ? '#9ca3af' : (isHovered ? brand.dangerHover : brand.danger),
      color: '#ffffff',
      border: 'none'
    },
    ghost: {
      backgroundColor: isHovered ? colors.bg.hover : 'transparent',
      color: colors.text.primary,
      border: 'none'
    },
    outline: {
      backgroundColor: isHovered ? colors.bg.hover : 'transparent',
      color: brand.primary,
      border: `2px solid ${brand.primary}`
    }
  }

  // Size styles
  const sizes = {
    sm: {
      padding: `${spacing.sm} ${spacing.md}`,
      fontSize: typography.fontSizes.sm,
      height: '32px'
    },
    md: {
      padding: `${spacing.sm} ${spacing.lg}`,
      fontSize: typography.fontSizes.base,
      height: '40px'
    },
    lg: {
      padding: `${spacing.md} ${spacing.xl}`,
      fontSize: typography.fontSizes.lg,
      height: '48px'
    }
  }

  const variantStyle = variants[variant] || variants.primary
  const sizeStyle = sizes[size] || sizes.md

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...variantStyle,
        ...sizeStyle,
        width: fullWidth ? '100%' : 'auto',
        borderRadius: radius.md,
        fontWeight: typography.fontWeights.medium,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.sm,
        transition: 'all 150ms ease',
        boxShadow: isHovered && !disabled ? shadows.sm : 'none',
        transform: isHovered && !disabled ? 'translateY(-1px)' : 'none',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        ...style
      }}
      {...props}
    >
      {loading && (
        <div
          style={{
            width: '16px',
            height: '16px',
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite'
          }}
        />
      )}
      {children}
    </button>
  )
}

// ============================================================
// CARD COMPONENT
// ============================================================
export const Card = ({
  children,
  elevated = false,
  hoverable = false,
  padding = 'md',
  onClick,
  style = {},
  ...props
}) => {
  const { colors, radius, spacing, shadows } = useTheme()
  const [isHovered, setIsHovered] = useState(false)

  const paddingSizes = {
    none: '0',
    sm: spacing.md,
    md: spacing.lg,
    lg: spacing.xl
  }

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: colors.bg.card,
        borderRadius: radius.lg,
        padding: paddingSizes[padding],
        border: `1px solid ${colors.border.primary}`,
        boxShadow: elevated ? shadows.md : (isHovered && hoverable ? shadows.lg : shadows.sm),
        transition: 'all 200ms ease',
        transform: isHovered && hoverable ? 'translateY(-2px)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        ...style
      }}
      {...props}
    >
      {children}
    </div>
  )
}

// ============================================================
// BADGE COMPONENT
// ============================================================
export const Badge = ({
  children,
  variant = 'default',
  size = 'md',
  style = {},
  ...props
}) => {
  const { spacing, typography, colors, darkMode } = useTheme()
  const { isModernLayout } = useLayout()

  const modernVariants = darkMode ? {
    current: { background: 'rgba(58, 171, 110, 0.12)', color: '#3aab6e', boxShadow: 'none', border: 'none' },
    dueSoon: { background: 'rgba(212, 160, 32, 0.12)', color: '#d4a020', boxShadow: 'none', border: 'none' },
    overdue: { background: 'rgba(212, 80, 80, 0.12)',  color: '#d45050', boxShadow: 'none', border: 'none' },
    never:   { background: 'rgba(122, 150, 176, 0.12)', color: '#7a96b0', boxShadow: 'none', border: 'none' },
  } : {
    current: { background: 'rgba(42, 122, 80, 0.1)', color: '#2a7a50', boxShadow: 'none', border: 'none' },
    dueSoon: { background: 'rgba(138, 98, 0, 0.1)', color: '#8a6200', boxShadow: 'none', border: 'none' },
    overdue: { background: 'rgba(138, 42, 42, 0.1)', color: '#8a2a2a', boxShadow: 'none', border: 'none' },
    never:   { background: 'rgba(90, 104, 120, 0.1)', color: '#5a6878', boxShadow: 'none', border: 'none' },
  }

  const classicVariants = {
    current: {
      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.75) 0%, rgba(5, 150, 105, 0.75) 100%)',
      color: '#ffffff',
      boxShadow: '0 2px 6px rgba(16, 185, 129, 0.2)',
      border: 'none'
    },
    dueSoon: {
      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.75) 0%, rgba(245, 158, 11, 0.75) 100%)',
      color: '#ffffff',
      boxShadow: '0 2px 6px rgba(245, 158, 11, 0.2)',
      border: 'none'
    },
    overdue: {
      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.75) 0%, rgba(220, 38, 38, 0.75) 100%)',
      color: '#ffffff',
      boxShadow: '0 2px 6px rgba(239, 68, 68, 0.2)',
      border: 'none'
    },
    never: {
      background: 'linear-gradient(135deg, rgba(156, 163, 175, 0.75) 0%, rgba(107, 114, 128, 0.75) 100%)',
      color: '#ffffff',
      boxShadow: '0 2px 6px rgba(156, 163, 175, 0.2)',
      border: 'none'
    },
    pending: {
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: '#ffffff',
      boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25)',
      border: 'none'
    },
    newOpening: {
      background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      color: '#ffffff',
      boxShadow: '0 2px 6px rgba(6, 182, 212, 0.25)',
      border: 'none'
    },
    default: {
      background: colors.bg.tertiary,
      color: colors.text.primary,
      boxShadow: 'none',
      border: `1px solid ${colors.border.primary}`
    }
  }

  const sizes = {
    sm: {
      padding: '0.375rem 0.875rem',
      fontSize: typography.fontSizes.xs,
      gap: '0.375rem'
    },
    md: {
      padding: isModernLayout ? '4px 12px' : `${spacing.sm} ${spacing.md}`,
      fontSize: isModernLayout ? '14px' : typography.fontSizes.sm,
      gap: spacing.xs
    }
  }

  const activeVariants = { ...classicVariants, ...modernVariants }

  const defaultVariant = { background: colors.bg.tertiary, color: colors.text.primary, boxShadow: 'none', border: `1px solid ${colors.border.primary}` }
  const variantStyle = activeVariants[variant] || defaultVariant
  const sizeStyle = sizes[size] || sizes.md

  return (
    <span
      style={{
        ...variantStyle,
        ...sizeStyle,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '20px',
        fontFamily: "Inter,ui-sans-serif,system-ui,sans-serif",
        fontWeight: typography.fontWeights.medium,
        whiteSpace: 'nowrap',
        textTransform: 'none',
        letterSpacing: '0.02em',
        ...style
      }}
      {...props}
    >
      {children}
    </span>
  )
}

// ============================================================
// INPUT COMPONENT
// ============================================================
export const Input = ({
  label,
  error,
  helperText,
  fullWidth = true,
  type = 'text',
  required = false,
  disabled = false,
  style = {},
  containerStyle = {},
  ...props
}) => {
  const { colors, radius, spacing, typography, brand } = useTheme()
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto', ...containerStyle }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: typography.fontSizes.sm,
            fontWeight: typography.fontWeights.medium,
            color: colors.text.secondary,
            marginBottom: spacing.sm
          }}
        >
          {label}
          {required && <span style={{ color: brand.danger, marginLeft: '2px' }}>*</span>}
        </label>
      )}
      <input
        type={type}
        disabled={disabled}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          width: '100%',
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: typography.fontSizes.base,
          color: colors.text.primary,
          backgroundColor: disabled ? colors.bg.tertiary : colors.input.bg,
          border: `2px solid ${error ? brand.danger : (isFocused ? colors.input.focus : colors.input.border)}`,
          borderRadius: radius.md,
          outline: 'none',
          transition: 'all 150ms ease',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          cursor: disabled ? 'not-allowed' : 'text',
          boxShadow: isFocused ? `0 0 0 3px ${error ? brand.danger : brand.primary}20` : 'none',
          ...style
        }}
        {...props}
      />
      {(error || helperText) && (
        <div
          style={{
            marginTop: spacing.sm,
            fontSize: typography.fontSizes.sm,
            color: error ? brand.danger : colors.text.tertiary
          }}
        >
          {error || helperText}
        </div>
      )}
    </div>
  )
}

// ============================================================
// TEXTAREA COMPONENT
// ============================================================
export const Textarea = ({
  label,
  error,
  helperText,
  fullWidth = true,
  required = false,
  disabled = false,
  rows = 4,
  style = {},
  containerStyle = {},
  ...props
}) => {
  const { colors, radius, spacing, typography, brand } = useTheme()
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto', ...containerStyle }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: typography.fontSizes.sm,
            fontWeight: typography.fontWeights.medium,
            color: colors.text.secondary,
            marginBottom: spacing.sm
          }}
        >
          {label}
          {required && <span style={{ color: brand.danger, marginLeft: '2px' }}>*</span>}
        </label>
      )}
      <textarea
        rows={rows}
        disabled={disabled}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          width: '100%',
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: typography.fontSizes.base,
          color: colors.text.primary,
          backgroundColor: disabled ? colors.bg.tertiary : colors.input.bg,
          border: `2px solid ${error ? brand.danger : (isFocused ? colors.input.focus : colors.input.border)}`,
          borderRadius: radius.md,
          outline: 'none',
          transition: 'all 150ms ease',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          resize: 'vertical',
          cursor: disabled ? 'not-allowed' : 'text',
          boxShadow: isFocused ? `0 0 0 3px ${error ? brand.danger : brand.primary}20` : 'none',
          lineHeight: typography.lineHeights.normal,
          ...style
        }}
        {...props}
      />
      {(error || helperText) && (
        <div
          style={{
            marginTop: spacing.sm,
            fontSize: typography.fontSizes.sm,
            color: error ? brand.danger : colors.text.tertiary
          }}
        >
          {error || helperText}
        </div>
      )}
    </div>
  )
}

// ============================================================
// SELECT COMPONENT
// ============================================================
export const Select = ({
  label,
  error,
  helperText,
  fullWidth = true,
  required = false,
  disabled = false,
  options = [],
  style = {},
  containerStyle = {},
  ...props
}) => {
  const { colors, radius, spacing, typography, brand } = useTheme()
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto', ...containerStyle }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: typography.fontSizes.sm,
            fontWeight: typography.fontWeights.medium,
            color: colors.text.secondary,
            marginBottom: spacing.sm
          }}
        >
          {label}
          {required && <span style={{ color: brand.danger, marginLeft: '2px' }}>*</span>}
        </label>
      )}
      <select
        disabled={disabled}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          width: '100%',
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: typography.fontSizes.base,
          color: colors.text.primary,
          backgroundColor: disabled ? colors.bg.tertiary : colors.input.bg,
          border: `2px solid ${error ? brand.danger : (isFocused ? colors.input.focus : colors.input.border)}`,
          borderRadius: radius.md,
          outline: 'none',
          transition: 'all 150ms ease',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow: isFocused ? `0 0 0 3px ${error ? brand.danger : brand.primary}20` : 'none',
          ...style
        }}
        {...props}
      >
        {options.map((option, index) => (
          <option key={index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {(error || helperText) && (
        <div
          style={{
            marginTop: spacing.sm,
            fontSize: typography.fontSizes.sm,
            color: error ? brand.danger : colors.text.tertiary
          }}
        >
          {error || helperText}
        </div>
      )}
    </div>
  )
}

// ============================================================
// DIVIDER COMPONENT
// ============================================================
export const Divider = ({ style = {}, ...props }) => {
  const { colors, spacing } = useTheme()

  return (
    <hr
      style={{
        border: 'none',
        borderTop: `1px solid ${colors.border.primary}`,
        margin: `${spacing.lg} 0`,
        ...style
      }}
      {...props}
    />
  )
}

// ============================================================
// EMPTY STATE COMPONENT
// ============================================================
export const EmptyState = ({
  icon,
  title,
  description,
  action,
  style = {},
  ...props
}) => {
  const { colors, spacing, typography } = useTheme()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing['3xl']} ${spacing.xl}`,
        textAlign: 'center',
        ...style
      }}
      {...props}
    >
      {icon && (
        <div
          style={{
            fontSize: '3rem',
            marginBottom: spacing.lg,
            opacity: 0.5
          }}
        >
          {icon}
        </div>
      )}
      {title && (
        <h3
          style={{
            fontSize: typography.fontSizes.xl,
            fontWeight: typography.fontWeights.semibold,
            color: colors.text.primary,
            margin: 0,
            marginBottom: spacing.sm
          }}
        >
          {title}
        </h3>
      )}
      {description && (
        <p
          style={{
            fontSize: typography.fontSizes.base,
            color: colors.text.tertiary,
            margin: 0,
            marginBottom: spacing.lg,
            maxWidth: '400px'
          }}
        >
          {description}
        </p>
      )}
      {action && <div>{action}</div>}
    </div>
  )
}

// ============================================================
// EMAIL CHIPS COMPONENT
// ============================================================
export const EmailChips = ({
  emails = [],
  onChange,
  placeholder = 'Add email address...',
  disabled = false
}) => {
  const { colors, spacing, radius, typography, darkMode } = useTheme()
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState('')

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const addEmail = (emailToAdd) => {
    const trimmed = emailToAdd.trim().toLowerCase()
    if (!trimmed) return

    if (!validateEmail(trimmed)) {
      setError('Invalid email format')
      return
    }

    if (emails.includes(trimmed)) {
      setError('Email already added')
      return
    }

    setError('')
    onChange([...emails, trimmed])
    setInputValue('')
  }

  const removeEmail = (emailToRemove) => {
    onChange(emails.filter(e => e !== emailToRemove))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmail(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      removeEmail(emails[emails.length - 1])
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const pastedEmails = pastedText.split(/[,;\s]+/).filter(Boolean)

    const validEmails = []
    pastedEmails.forEach(email => {
      const trimmed = email.trim().toLowerCase()
      if (validateEmail(trimmed) && !emails.includes(trimmed) && !validEmails.includes(trimmed)) {
        validEmails.push(trimmed)
      }
    })

    if (validEmails.length > 0) {
      onChange([...emails, ...validEmails])
    }
    setInputValue('')
  }

  const handleAddClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (inputValue.trim()) {
      addEmail(inputValue)
    }
  }

  return (
    <div>
      {/* Email chips display */}
      {emails.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: spacing.sm,
            marginBottom: spacing.sm
          }}
        >
          {emails.map((email) => (
            <span
              key={email}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacing.sm,
                padding: `${spacing.sm} ${spacing.md}`,
                backgroundColor: darkMode ? 'rgba(30,64,175,0.3)' : '#dbeafe',
                color: darkMode ? '#93c5fd' : '#1e40af',
                borderRadius: radius.full,
                fontSize: 'clamp(0.8125rem, 2.5vw, 0.875rem)',
                fontWeight: typography.fontWeights.medium
              }}
            >
              {email}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeEmail(email)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    minWidth: '24px',
                    minHeight: '24px',
                    padding: 0,
                    border: 'none',
                    borderRadius: '50%',
                    backgroundColor: darkMode ? '#3b82f6' : '#1e40af',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    lineHeight: 1,
                    touchAction: 'manipulation'
                  }}
                  aria-label={`Remove ${email}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Input row with Add button */}
      <div
        style={{
          display: 'flex',
          gap: spacing.sm,
          alignItems: 'stretch'
        }}
      >
        <input
          type="email"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setError('')
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => { if (inputValue.trim()) addEmail(inputValue) }}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            flex: 1,
            minWidth: 0,
            padding: spacing.md,
            border: `1px solid ${error ? '#ef4444' : colors.border.primary}`,
            borderRadius: radius.md,
            backgroundColor: disabled ? colors.bg.tertiary : colors.bg.primary,
            fontSize: typography.fontSizes.base,
            color: colors.text.primary,
            minHeight: '48px',
            boxSizing: 'border-box'
          }}
        />
        <button
          type="button"
          onClick={handleAddClick}
          disabled={disabled || !inputValue.trim()}
          style={{
            padding: `${spacing.sm} ${spacing.lg}`,
            backgroundColor: (!disabled && inputValue.trim()) ? (darkMode ? '#3b82f6' : '#1e40af') : '#9ca3af',
            color: 'white',
            border: 'none',
            borderRadius: radius.md,
            fontSize: typography.fontSizes.base,
            fontWeight: typography.fontWeights.medium,
            cursor: (!disabled && inputValue.trim()) ? 'pointer' : 'not-allowed',
            minHeight: '48px',
            minWidth: '64px',
            touchAction: 'manipulation'
          }}
        >
          Add
        </button>
      </div>

      {error && (
        <p style={{
          marginTop: spacing.xs,
          fontSize: typography.fontSizes.sm,
          color: '#ef4444'
        }}>
          {error}
        </p>
      )}
    </div>
  )
}

// Named exports are used directly, this default export is for backward compatibility
// eslint-disable-next-line import/no-anonymous-default-export
export default { Button, Card, Badge, Input, Textarea, Select, Divider, EmptyState, EmailChips }
